/**
 * Timer and one-line attribution for a Work run. Tool steps and progress
 * notes render in the transcript as they happen, or fold into working notes
 * when the user wants a quieter log.
 */
import React, { useMemo } from 'react';
import { ActivityIndicator, View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import WorkAppear from '@/components/Coworker/WorkAppear';
import { useWorkNow } from '@/hooks/useWorkNow';
import {
  formatWorkDuration,
  formatWorkCostNote,
  isWorkResumable,
  workStatusMeta,
  workToneColor,
} from '@/constants/work';

function WorkRunHeader({
  durationMs = null,
  isLive = false,
  liveStartTs = null,
  status = null,
  activityLabel = null,
  activityIcon = null,
  attribution = null,
  credits = null,
  creditScope = 'run',
  creditPaused = false,
}) {
  const { colors } = useTheme();
  const now = useWorkNow(isLive);

  const elapsed = useMemo(() => (
    isLive
      ? (liveStartTs ? formatWorkDuration(now - liveStartTs) : null)
      : formatWorkDuration(durationMs)
  ), [isLive, liveStartTs, now, durationMs]);

  const wasInterrupted = !isLive && isWorkResumable(status);
  const outcome = wasInterrupted ? workStatusMeta(creditPaused ? 'interrupted' : status) : null;
  const outcomeColor = outcome
    ? workToneColor(outcome.tone, colors)
    : colors.color_success;
  const isStatusRow = isLive && !!activityLabel;
  let label = isLive
    ? (elapsed ? `Working for ${elapsed}` : 'Working')
    : (elapsed ? `Worked for ${elapsed}` : 'Worked on this');
  if (isStatusRow) {
    label = activityLabel;
  } else if (wasInterrupted) {
    if (creditPaused) label = elapsed ? `Paused for credits after ${elapsed}` : 'Paused for credits';
    else if (status === 'stopped') label = elapsed ? `Stopped after ${elapsed}` : 'Work stopped';
    else if (status === 'budget_exceeded') label = elapsed ? `Run limit reached after ${elapsed}` : 'Run limit reached';
    else label = elapsed ? `Paused after ${elapsed}` : 'Work paused';
  }
  const showAttribution = !!attribution && attribution !== label && !isStatusRow;
  // What the work cost, next to how long it took — settled receipts only
  // (a live tally would tick unevenly as calls bill at different moments).
  const costNote = (!isLive)
    ? formatWorkCostNote({ creditsUsed: credits, scope: creditScope })
    : null;
  const title = costNote ? `${label} · ${costNote}` : label;

  return (
    <View style={styles.container}>
      <View
        style={styles.header}
        accessibilityRole="text"
        accessibilityLabel={`${title}${showAttribution ? `. ${attribution}` : ''}`}
      >
        {isStatusRow ? (
          // Always spin while the run is live — a static tool icon looked
          // settled when the user left and came back mid-step.
          <ActivityIndicator size="small" color={colors.color_success} style={styles.headerIcon} />
        ) : isLive ? (
          <Icon name="clock" size={14} color={colors.color_success} style={styles.headerIcon} />
        ) : (
          <Icon name={outcome?.icon || 'circle-check'} size={14} color={outcomeColor} style={styles.headerIcon} />
        )}
        <View style={styles.headerText}>
          <View style={styles.headerTitleRow}>
            {/* Keyed on the live status label so each change of activity
                eases in rather than hard-swapping. The elapsed suffix
                updates inside the same node without replaying the fade. */}
            <WorkAppear
              key={isStatusRow ? `status:${label}` : 'label'}
              enabled={isStatusRow}
              duration={160}
              distance={3}
              style={styles.labelWrap}
            >
              <Text size="s" color="primary" weight="medium">
                {title}
                {isStatusRow && elapsed && (
                  <Text size="s" color="secondary">{` · ${elapsed}`}</Text>
                )}
              </Text>
            </WorkAppear>
          </View>
          {showAttribution && (
            <Text size="s" color="secondary" numberOfLines={3} style={styles.attribution}>
              {attribution}
            </Text>
          )}
        </View>
      </View>
      <View style={[styles.divider, { backgroundColor: colors.border_color_secondary }]} />
    </View>
  );
}

WorkRunHeader.propTypes = {
  durationMs: PropTypes.number,
  isLive: PropTypes.bool,
  liveStartTs: PropTypes.number,
  status: PropTypes.string,
  activityLabel: PropTypes.string,
  activityIcon: PropTypes.string,
  attribution: PropTypes.string,
  credits: PropTypes.number,
  creditPaused: PropTypes.bool,
  creditScope: PropTypes.oneOf(['run', 'task']),
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
    marginBottom: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    alignSelf: 'flex-start',
    minHeight: 28,
    paddingHorizontal: 7,
    paddingVertical: 4,
    marginLeft: -7,
    borderRadius: 8,
    maxWidth: '100%',
  },
  headerText: {
    flexShrink: 1,
    gap: 4,
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  labelWrap: {
    flexShrink: 1,
  },
  attribution: {
    maxWidth: 520,
  },
  headerIcon: {
    marginTop: 3,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    width: '100%',
  },
});

export default React.memo(WorkRunHeader);
